import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useBilling();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing_session" });
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        setState({ status: data.payment_status, attempts, raw: data });
        if (data.payment_status === "paid") {
          await refresh();
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired" || attempts >= maxAttempts) {
          setState({ status: data.payment_status || "expired", attempts, raw: data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", attempts, error: e?.response?.data?.detail || String(e) });
      }
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-champagne flex items-center justify-center px-6">
      <div data-testid="billing-success-card" className="w-full max-w-lg bg-white border border-stone p-10">
        {state.status === "polling" && (
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-paars-dark mx-auto mb-4 animate-spin" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-2">// VERIFYING_PAYMENT</div>
            <h1 className="text-2xl font-bold text-graphite tracking-tight mb-2">One moment</h1>
            <p className="text-sm text-graphite/65">Confirming payment with Stripe…</p>
          </div>
        )}
        {state.status === "paid" && (
          <div className="text-center">
            <div className="w-14 h-14 bg-groen flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-graphite" strokeWidth={2.5} />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-groen-dark font-bold mb-2">// PAYMENT_CONFIRMED</div>
            <h1 className="text-2xl font-bold text-graphite tracking-tight mb-2">Welcome to Pro.</h1>
            <p className="text-sm text-graphite/65 mb-6">
              Your tracker is unlocked. Email alerts, hotlists and unlimited holdings are now active.
            </p>
            <button
              data-testid="billing-success-continue"
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2.5 bg-paars text-graphite font-semibold text-xs tracking-wider uppercase hover:bg-paars-light flex items-center gap-2 mx-auto"
            >
              Open Tracker <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {(state.status === "expired" || state.status === "error" || state.status === "missing_session") && (
          <div className="text-center">
            <div className="w-14 h-14 bg-burgundy/15 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-7 h-7 text-burgundy" strokeWidth={2.2} />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-burgundy font-bold mb-2">// PAYMENT_NOT_CONFIRMED</div>
            <h1 className="text-2xl font-bold text-graphite tracking-tight mb-2">Something went wrong</h1>
            <p className="text-sm text-graphite/65 mb-6">
              {state.error || "We couldn't confirm your payment. If you were charged, contact support."}
            </p>
            <button
              onClick={() => navigate("/pricing")}
              className="px-5 py-2.5 border border-paars text-paars-dark font-semibold text-xs tracking-wider uppercase hover:bg-paars/10"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
